import { Activity, Coins, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { fetchAdminTicker } from '@/api/admin';

const money = (v) => `$${Number(v ?? 0).toFixed(2)}`;

/**
 * Pulsing real-time admin gains feed embedded in the header bar. Polls the
 * /api/admin/ticker endpoint every 60 seconds (slow-poll, 2025-06; matches
 * the live heartbeat cadence). Renders three live counters + a scrolling
 * marquee of the most recent $0.25 increments (cycle fees + orphan matches).
 */
export const AdminTicker = () => {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(0);
  const prevTotalRef = useRef('0.00');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const next = await fetchAdminTicker();
        if (!alive) return;
        if (prevTotalRef.current !== next.admin_pool_usd) {
          prevTotalRef.current = next.admin_pool_usd;
          setPulse((p) => p + 1);
        }
        setData(next);
      } catch {
        /* swallow — reattempt on next tick */
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const recent = data?.recent_gains || [];
  const marqueeItems = recent.length
    ? [...recent, ...recent]
    : Array.from({ length: 6 }).map((_, i) => ({
        transaction_id: `placeholder-${i}`,
        type: 'Cycle',
        amount: '0.25',
        metadata: { reason: 'awaiting_traffic' },
      }));

  return (
    <div className="sc-admin-ticker" data-testid="admin-ticker">
      <div className="sc-admin-ticker__metrics">
        <div className={`sc-admin-ticker__chip ${pulse % 2 === 0 ? '' : 'is-flash'}`} data-testid="ticker-admin-pool">
          <Coins className="w-3.5 h-3.5" />
          <span className="sc-admin-ticker__label">Admin Pool</span>
          <span className="sc-admin-ticker__value">{money(data?.admin_pool_usd)}</span>
        </div>
        <div className="sc-admin-ticker__chip" data-testid="ticker-today-cycle">
          <Activity className="w-3.5 h-3.5" />
          <span className="sc-admin-ticker__label">24h Fees</span>
          <span className="sc-admin-ticker__value">{money(data?.today_cycle_fees_usd)}</span>
        </div>
        <div className="sc-admin-ticker__chip" data-testid="ticker-today-orphan">
          <Zap className="w-3.5 h-3.5" />
          <span className="sc-admin-ticker__label">24h Orphans</span>
          <span className="sc-admin-ticker__value">{money(data?.today_orphan_matches_usd)}</span>
        </div>
        <div className="sc-admin-ticker__chip" data-testid="ticker-cycles">
          <span className="sc-dot" style={{ background: '#FFC20E' }} />
          <span className="sc-admin-ticker__label">Cycles</span>
          <span className="sc-admin-ticker__value sc-tabular">{Number(data?.pool?.total_cycles_completed ?? 0).toLocaleString()}</span>
        </div>
        <div className="sc-admin-ticker__chip" data-testid="ticker-pool-size">
          <span className="sc-admin-ticker__label">Pool</span>
          <span className="sc-admin-ticker__value sc-tabular">
            {(data?.pool?.active_rotator_pool_size_fillers ?? 0) + (data?.pool?.active_rotator_pool_size_roots ?? 0)}
          </span>
        </div>
      </div>

      <div className="sc-admin-ticker__marquee" aria-hidden="true">
        <div className="sc-admin-ticker__marquee-track">
          {marqueeItems.map((row, idx) => {
            const label = row.type === 'Match' ? 'ORPHAN MATCH' : row?.metadata?.reason === 'orphan' ? 'ORPHAN MATCH' : 'PLATFORM FEE';
            return (
              <span key={`${row.transaction_id}-${idx}`} className="sc-admin-ticker__entry">
                <span className="sc-admin-ticker__plus">+{money(row.amount)}</span>
                <span className="sc-admin-ticker__entry-label">{label}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminTicker;
